import axios from 'axios'

export type UtiliteitnetStatus = 'concept' | 'planning' | 'in_aanleg' | 'productie' | 'gearchiveerd'

/** Dutch display labels, in lifecycle order. Only 'productie' nets feed KLIC. */
export const UTILITEITNET_STATUS_LABELS: Record<UtiliteitnetStatus, string> = {
  concept: 'Concept',
  planning: 'Planning',
  in_aanleg: 'In aanleg',
  productie: 'Productie',
  gearchiveerd: 'Gearchiveerd',
}

export interface Utiliteitsnet {
  id: string
  naam: string
  beschrijving: string | null
  status: UtiliteitnetStatus
  /** Per-net drawing defaults, keyed by draw type (shape owned by the editor). */
  tekenStandaarden?: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export interface CreateUtiliteitnetDto {
  naam: string
  beschrijving?: string
  status?: UtiliteitnetStatus
  tekenStandaarden?: Record<string, unknown>
}

export interface UpdateUtiliteitnetDto {
  naam?: string
  beschrijving?: string
  status?: UtiliteitnetStatus
  tekenStandaarden?: Record<string, unknown>
}

export type UtiliteitnetOrderField = 'naam' | 'status' | 'createdAt'

export interface FilterUtiliteitnetDto {
  naam?: string
  status?: UtiliteitnetStatus
  orderBy?: UtiliteitnetOrderField
  orderDirection?: 'ASC' | 'DESC'
  page?: number
  limit?: number
}

export interface PaginatedUtiliteitsnetten {
  total_rows: number
  total_pages: number
  page: number
  limit: number
  data: Utiliteitsnet[]
}

const BASE = '/api/grondwerk/netten'

export async function getUtiliteitsnetten(filter?: FilterUtiliteitnetDto): Promise<PaginatedUtiliteitsnetten> {
  const response = await axios.get(BASE, { params: filter })
  return response.data
}

export async function getUtiliteitsnet(id: string): Promise<Utiliteitsnet> {
  const response = await axios.get(`${BASE}/${id}`)
  return response.data
}

export async function createUtiliteitsnet(dto: CreateUtiliteitnetDto): Promise<Utiliteitsnet> {
  const response = await axios.post(BASE, dto)
  return response.data
}

export async function updateUtiliteitsnet(id: string, dto: UpdateUtiliteitnetDto): Promise<Utiliteitsnet> {
  const response = await axios.patch(`${BASE}/${id}`, dto)
  return response.data
}

export async function deleteUtiliteitsnet(id: string): Promise<void> {
  await axios.delete(`${BASE}/${id}`)
}

// --- Project assignment (drives the editor's auto-fit to connections) ---

export interface NetProject {
  projectId: string
  name: string
}

/** Minimal project shape returned by the project search used in the picker. */
export interface ProjectSummary {
  id: string
  name: string
}

/** RD-New (EPSG:28992) bbox of a drawing's assigned projects' connections. */
export interface ConnectieBounds {
  bounds: { minX: number, minY: number, maxX: number, maxY: number } | null
  count: number
}

export async function getNetProjecten(id: string): Promise<NetProject[]> {
  const response = await axios.get(`${BASE}/${id}/projecten`)
  return response.data
}

export async function setNetProjecten(id: string, projectIds: string[]): Promise<NetProject[]> {
  const response = await axios.put(`${BASE}/${id}/projecten`, { projectIds })
  return response.data
}

export async function getNetConnectieBounds(id: string): Promise<ConnectieBounds> {
  const response = await axios.get(`${BASE}/${id}/connecties-bounds`)
  return response.data
}

// ── Presence + activity (who else is in a net; has it changed since I loaded it) ──

/** One browser tab currently in a net's editor. */
export interface NetPresenceEntry {
  clientId: string
  userId: string
  naam: string
  lastSeen: string
}

export interface NetActivity {
  /**
   * Opaque server token for the net's latest change (creates, updates and
   * deletes across all canvas objects); null for a net without objects.
   * Compare for equality or pass back as `since` — never parse it.
   */
  lastModifiedAt: string | null
  /** Top-level objects touched after `since`; 0 when no `since` was sent. */
  changedCount: number
}

export interface NetPresenceStatus {
  present: NetPresenceEntry[]
  activity: NetActivity
}

/** Register this tab's heartbeat; returns everyone present and the net's activity. */
export async function heartbeatNetPresence(id: string, clientId: string, since?: string | null): Promise<NetPresenceStatus> {
  const response = await axios.post(`${BASE}/${id}/presence`, { clientId, since: since ?? undefined })
  return response.data
}

export async function leaveNetPresence(id: string, clientId: string): Promise<void> {
  await axios.delete(`${BASE}/${id}/presence/${clientId}`)
}

export async function getNetActivity(id: string, since?: string | null): Promise<NetActivity> {
  const response = await axios.get(`${BASE}/${id}/activity`, { params: since ? { since } : undefined })
  return response.data
}

/** Search projects by name for the assignment picker. */
export async function searchProjecten(naam: string): Promise<ProjectSummary[]> {
  const response = await axios.get('/api/projects', { params: { naam, limit: 25, page: 0 } })
  // The projects list endpoint returns a paginated envelope; tolerate either the
  // envelope or a bare array and map down to the fields the picker needs.
  const rows = Array.isArray(response.data) ? response.data : (response.data?.data ?? response.data?.result ?? [])
  return rows.map((p: { id: string, name: string }) => ({ id: p.id, name: p.name }))
}
